import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isApproved, setIsApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Wrap any promise in a timeout. If the inner promise hasn't resolved within
  // `ms` milliseconds, reject so the UI never hangs indefinitely on a slow
  // Firestore/Auth call.
  const withTimeout = (promise, ms, label) =>
    Promise.race([
      promise,
      new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
    ]);

  // Check user approval status from Firestore (5s budget — read should be ~ms)
  const checkApprovalStatus = async (uid) => {
    if (!db) {
      console.warn('Firestore not initialized');
      return false;
    }

    try {
      const userDoc = await withTimeout(getDoc(doc(db, 'approved_users', uid)), 5000, 'checkApprovalStatus');
      if (userDoc.exists()) {
        return userDoc.data().approved === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking approval status:', error);
      return false;
    }
  };

  // Create user document in approved_users collection
  const createUserDocument = async (uid, email) => {
    if (!db) {
      console.warn('Firestore not initialized');
      return;
    }

    try {
      await setDoc(doc(db, 'approved_users', uid), {
        email,
        approved: false,
        createdAt: serverTimestamp(),
        role: 'user'
      });
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    // Safety: if onAuthStateChanged hasn't fired in 8s (corrupted IndexedDB,
    // network blip, etc.), give up and let ProtectedRoute redirect to /login.
    // Without this the app can hang indefinitely on the loader.
    const timeoutId = setTimeout(() => {
      console.warn('[auth] onAuthStateChanged did not fire within 8s — releasing loader');
      setLoading(false);
    }, 8000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(timeoutId);
      if (firebaseUser) {
        setUser(firebaseUser);
        const approved = await checkApprovalStatus(firebaseUser.uid);
        setIsApproved(approved);
      } else {
        setUser(null);
        setIsApproved(false);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  // Login. Approval check is intentionally NOT awaited here — onAuthStateChanged
  // fires after signInWithEmailAndPassword resolves and runs the approval check
  // itself. Doing it twice doubled the time on screen for the "Signing in…"
  // spinner. The 30s timeout is a safety net against a hung browser environment
  // (corrupted IndexedDB, etc.), not the network round trip.
  const login = async (email, password) => {
    if (!auth) {
      throw new Error('Firebase auth not initialized');
    }

    setError(null);
    try {
      const result = await withTimeout(
        signInWithEmailAndPassword(auth, email, password),
        30000,
        'signIn',
      );
      return result.user;
    } catch (error) {
      // Distinguish timeout (no error.code) from real Firebase auth errors
      const msg = error.code
        ? getErrorMessage(error.code)
        : `Sign-in is taking longer than expected. ${error.message}`;
      setError(msg);
      throw error;
    }
  };

  // Signup function
  const signup = async (email, password) => {
    if (!auth) {
      throw new Error('Firebase auth not initialized');
    }

    setError(null);
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Try to create user document, but don't fail signup if it fails
      try {
        await createUserDocument(result.user.uid, email);
      } catch (docError) {
        console.warn('Could not create user document (user can still be approved manually):', docError);
      }
      setIsApproved(false);
      return result.user;
    } catch (error) {
      console.error('Signup error:', error.code, error.message);
      setError(getErrorMessage(error.code, 'signup'));
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    if (!auth) {
      throw new Error('Firebase auth not initialized');
    }

    try {
      await signOut(auth);
      setUser(null);
      setIsApproved(false);
    } catch (error) {
      setError(getErrorMessage(error.code));
      throw error;
    }
  };

  // Password reset function
  const resetPassword = async (email) => {
    if (!auth) {
      throw new Error('Firebase auth not initialized');
    }

    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      setError(getErrorMessage(error.code));
      throw error;
    }
  };

  // Refresh approval status
  const refreshApprovalStatus = async () => {
    if (user) {
      const approved = await checkApprovalStatus(user.uid);
      setIsApproved(approved);
      return approved;
    }
    return false;
  };

  // Convert Firebase error codes to user-friendly messages
  const getErrorMessage = (code, context = 'login') => {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please login instead.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/operation-not-allowed':
        return 'Email/password sign in is not enabled. Please contact an administrator.';
      case 'auth/configuration-not-found':
        return 'Authentication is not configured. Please enable Email/Password in Firebase Console.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact an administrator.';
      case 'auth/user-not-found':
        return 'USER_NOT_FOUND'; // Special marker for LoginPage to handle
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-credential':
        return context === 'login'
          ? 'USER_NOT_FOUND' // Could be wrong email or password - suggest signup
          : 'Invalid credentials. Please try again.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection.';
      default:
        console.error('Unhandled auth error code:', code);
        return 'An error occurred. Please try again.';
    }
  };

  const value = {
    user,
    isApproved,
    loading,
    error,
    login,
    signup,
    logout,
    resetPassword,
    refreshApprovalStatus,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
